import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import SectionHeading from "@/components/feature/SectionHeading";
import { usePageMeta } from "@/hooks/usePageMeta";
import { getAllMovies, getHotMovies, searchMovies } from "@/utils/movies";

const typeFilters = [
  { value: "all", label: "全部" },
  { value: "movie", label: "电影" },
  { value: "tv", label: "电视剧" },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("keyword") || "";
  const activeType = searchParams.get("type") || "all";
  const [input, setInput] = useState(keyword);

  usePageMeta(
    keyword ? `“${keyword}”的搜索结果 - 星辰影视` : "搜索影视作品 - 星辰影视",
    keyword
      ? `星辰影视关于“${keyword}”的影视搜索结果显示，可按电影或电视剧筛选查看匹配作品。`
      : "在星辰影视搜索电影与电视剧，快速找到想看的影视作品。",
    "影视搜索,电影搜索,电视剧搜索,星辰影视",
  );

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const matched = useMemo(() => searchMovies(getAllMovies(), keyword), [keyword]);
  const result = useMemo(
    () => (activeType === "all" ? matched : matched.filter((item) => item.type === activeType)),
    [matched, activeType],
  );
  const hotList = useMemo(() => getHotMovies(4), []);

  const submit = () => {
    const word = input.trim();
    const next = new URLSearchParams();
    if (word) next.set("keyword", word);
    setSearchParams(next);
  };

  const setType = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === "all") next.delete("type");
    else next.set("type", value);
    setSearchParams(next);
  };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-10">
      <Breadcrumb items={[{ label: "搜索" }]} />

      <div className="mt-5 bg-background-50 rounded-lg border border-background-200 p-5 md:p-6">
        <h1 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">
          搜索影视作品
        </h1>
        <div className="mt-4 flex items-center bg-background-100 rounded-md border border-background-300 pl-4 pr-1 h-12 max-w-2xl">
          <i className="ri-search-line text-foreground-500 text-lg" aria-hidden="true" />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
            }}
            placeholder="输入影视名称、导演、类型，如：科幻、长安秘录"
            className="flex-1 bg-transparent px-3 text-sm md:text-base text-foreground-900 placeholder:text-foreground-400 outline-none"
          />
          <button
            type="button"
            onClick={submit}
            className="h-10 px-5 rounded-md bg-accent-500 hover:bg-accent-600 text-white text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
          >
            搜索
          </button>
        </div>

        {keyword && (
          <p className="mt-4 text-sm text-foreground-600">
            当前搜索词：
            <strong className="text-primary-700">“{keyword}”</strong>
            <span className="mx-2 text-foreground-300">|</span>
            共找到 <strong className="text-accent-600 font-label">{result.length}</strong> 部相关作品
          </p>
        )}
      </div>

      {keyword && (
        <div className="mt-6 flex items-center gap-2 flex-wrap">
          <span className="text-sm text-foreground-500">类型筛选：</span>
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-200">
            {typeFilters.map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => setType(item.value)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeType === item.value
                    ? "bg-background-50 text-primary-700"
                    : "text-foreground-600 hover:text-foreground-900"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {keyword && result.length > 0 && (
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
          {result.map((movie) => (
            <MovieCard key={movie.id} movie={movie} />
          ))}
        </div>
      )}

      {keyword && result.length === 0 && (
        <div className="mt-8 bg-background-50 rounded-lg border border-background-200 py-14 px-4 flex flex-col items-center text-center">
          <span className="w-14 h-14 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
            <i className="ri-search-eye-line text-3xl" aria-hidden="true" />
          </span>
          <p className="mt-4 text-foreground-800 font-medium">
            没有找到与“{keyword}”相关的作品
          </p>
          <p className="mt-1.5 text-sm text-foreground-500">
            换个关键词试试，或看看下面的热门推荐
          </p>
        </div>
      )}

      {!keyword && (
        <div className="mt-8 bg-background-50 rounded-lg border border-background-200 py-14 px-4 flex flex-col items-center text-center">
          <span className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
            <i className="ri-search-line text-3xl" aria-hidden="true" />
          </span>
          <p className="mt-4 text-foreground-800 font-medium">输入关键词开始搜索</p>
          <p className="mt-1.5 text-sm text-foreground-500">支持按影视名称、导演、地区与类型查找</p>
        </div>
      )}

      {(result.length === 0 || !keyword) && (
        <section className="mt-12">
          <SectionHeading title="热门推荐" subtitle="近期热度较高的影视作品" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {hotList.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}